// ============================================================================
// Shared tender UX config — the single source of truth that replaces the gate
// codes (G1–G5) with plain approval-step names, and defines the tender status
// pills. Used by the Tender Explorer, the Tender Workbench, and My Approvals.
// No "G1/G2/.." or "BRD/TDD" strings should appear in the UI; use these labels.
// ============================================================================

import type { TenderActivity, GateState } from './types'
import { TENDER_STAGES } from './types'

// ---- The five human-in-the-loop approval steps (plain names) ----------------
export interface ApprovalStep {
  gate: string          // internal id (G1..G5) — never shown to the user
  label: string         // UI name
  role: string          // human-readable role
  roleKeys: string[]    // Keycloak roles that can approve
  approves: string      // one-line "what you're approving"
  stage: number         // lifecycle stage it sits at
}

export const APPROVAL_STEPS: ApprovalStep[] = [
  { gate: 'G1', label: 'Route & Market Approval', role: 'Senior Responsible Officer', roleKeys: ['tender-sro'], approves: 'Business case + recommended route, before going to market', stage: 2 },
  { gate: 'G2', label: 'Clarification Approval', role: 'Procurement Officer', roleKeys: ['tender-procurement-officer'], approves: 'AI-drafted clarification responses before they are issued', stage: 4 },
  { gate: 'G3', label: 'Evaluation Lock', role: 'Moderator / SRO', roleKeys: ['tender-sro', 'tender-moderator'], approves: 'Scores, flags and conflict-of-interest prompt — lock the evaluation', stage: 5 },
  { gate: 'G4', label: 'Award Approval', role: 'Governance', roleKeys: ['tender-governance'], approves: 'Contract pack + most-advantageous-tender rationale, before award', stage: 6 },
  { gate: 'G5', label: 'Post-Award Review', role: 'Senior Responsible Officer', roleKeys: ['tender-sro'], approves: 'KPI breach + service credit + performance assessment', stage: 8 },
]

export const STEP_BY_GATE: Record<string, ApprovalStep> = Object.fromEntries(
  APPROVAL_STEPS.map(s => [s.gate, s]),
)

/** Stage at which each gate decision is taken — mirrors backend GATE_DECISION_STAGE. */
export const GATE_DECISION_STAGE: Record<string, number> = {
  G1: 1, G2: 4, G3: 5, G4: 6, G5: 8,
}

/**
 * Unresolved BLOCK checks that inform a gate (tagged gate_id or untagged at decision stage).
 * Mirrors backend _open_blocks filtering for the Decision panel override path.
 */
export function policyBlocksForGate(
  policyChecks: TenderActivity['policy_checks'],
  gateId: string,
): NonNullable<TenderActivity['policy_checks']> {
  const ds = GATE_DECISION_STAGE[gateId]
  if (ds == null) return []
  return (policyChecks ?? []).filter((c) => {
    if (c.result !== 'BLOCK') return false
    if (c.gate_id) return c.gate_id === gateId
    return c.stage === ds
  })
}

/**
 * Maps each approval gate to the workbench tab that shows the work being
 * approved, so the Overview approval-workflow steps can deep-link into context.
 * - G1 (Route & Market Approval) → Business Case
 * - G2 (Clarification Approval)  → Tender Details (clarification responses)
 * - G3 (Evaluation Lock)         → Evaluation
 * - G4 (Award Approval)          → Contract
 * - G5 (Post-Award Review)       → Contract
 */
export const GATE_TO_TAB: Record<string, string> = {
  G1: 'business_case',
  G2: 'details',
  G3: 'evaluation',
  G4: 'contract',
  G5: 'contract',
}

/** Plain name for a gate id (fallback to the id only if truly unknown). */
export function stepLabel(gate: string): string {
  return STEP_BY_GATE[gate]?.label ?? gate
}

/** Two-line labels for the journey-rail gate checkpoints (fits narrow columns). */
export function gateRailLines(gate: string): string[] {
  const lines: Record<string, string[]> = {
    G1: ['Route & Market', 'Approval'],
    G2: ['Clarification', 'Approval'],
    G3: ['Evaluation', 'Lock'],
    G4: ['Award', 'Approval'],
    G5: ['Post-Award', 'Review'],
  }
  return lines[gate] ?? [stepLabel(gate)]
}

// ---- Tender status pills (derived from stage + gate states) -----------------
export type TenderStatus =
  | 'draft' | 'in_progress' | 'needs_approval' | 'returned' | 'awarded' | 'completed' | 'cancelled'

export const TENDER_STATUS_CONFIG: Record<TenderStatus, { label: string; color: string; bgColor: string; dotColor: string }> = {
  draft:          { label: 'Draft',          color: 'text-slate-600',   bgColor: 'bg-slate-100',   dotColor: 'bg-slate-400' },
  in_progress:    { label: 'In Progress',    color: 'text-blue-700',    bgColor: 'bg-blue-50',     dotColor: 'bg-blue-500' },
  needs_approval: { label: 'Needs Approval', color: 'text-amber-700',   bgColor: 'bg-amber-50',    dotColor: 'bg-amber-500' },
  returned:       { label: 'Returned',       color: 'text-red-700',     bgColor: 'bg-red-50',      dotColor: 'bg-red-500' },
  awarded:        { label: 'Awarded',        color: 'text-emerald-700', bgColor: 'bg-emerald-50',  dotColor: 'bg-emerald-500' },
  completed:      { label: 'Completed',      color: 'text-teal-700',    bgColor: 'bg-teal-50',     dotColor: 'bg-teal-500' },
  cancelled:      { label: 'Cancelled',      color: 'text-slate-500',   bgColor: 'bg-slate-100',   dotColor: 'bg-slate-300' },
}

/** The gate that gates the current stage (or null). */
export function gateForStage(stage: number): string | null {
  return TENDER_STAGES.find(s => s.stage === stage)?.gate ?? null
}

/** Derive the plain tender status pill from stage + gate_states. */
export function deriveTenderStatus(t: Pick<TenderActivity, 'current_stage' | 'gate_states'>): TenderStatus {
  const gs = t.gate_states || {}
  const stage = t.current_stage ?? 0
  if (Object.values(gs).some(g => g?.status === 'returned')) return 'returned'
  // The two gates the six-stage lifecycle added (Discover and Define): a legacy row never has them.
  if (gs['GD']?.status === 'pending' || gs['GR']?.status === 'pending') return 'needs_approval'
  // A contract in Manage with its cycle open (Leo's review in progress) is live, not complete.
  if (stage >= 9) return (gs['G5']?.status as string | undefined) === 'in_progress' ? 'in_progress' : 'completed'
  const curGate = gateForStage(stage)
  if (curGate && gs[curGate]?.status === 'pending') return 'needs_approval'
  if (gs['G4']?.status === 'approved' || stage >= 7) return 'awarded'
  if (stage === 0 && Object.keys(gs).length === 0) return 'draft'
  return 'in_progress'
}

/** The currently-pending approval step for a tender, if any. */
export function pendingStep(t: Pick<TenderActivity, 'current_stage' | 'gate_states'>): ApprovalStep | null {
  const gs = t.gate_states || {}
  const curGate = gateForStage(t.current_stage ?? 0)
  if (curGate && gs[curGate]?.status === 'pending') return STEP_BY_GATE[curGate] ?? null
  // fall back to any pending gate
  for (const step of APPROVAL_STEPS) {
    if (gs[step.gate]?.status === 'pending') return step
  }
  return null
}

/** True when every approval workflow step (G1–G5) has been approved. */
export function allApprovalStepsApproved(gs: Record<string, GateState>): boolean {
  return APPROVAL_STEPS.every(s => gs[s.gate]?.status === 'approved')
}

/** Evaluation Lock (G3) has been approved by a human approver. */
export function evaluationLockApproved(gs: Record<string, GateState> = {}): boolean {
  return gs.G3?.status === 'approved'
}

/** At least one bid has been scored via the Evaluate Bids workflow step. */
export function bidsEvaluated(bids: TenderActivity['bids']): boolean {
  return (bids ?? []).some(b => b.status === 'evaluated')
}

/** Evaluate Bids may run once Evaluation Lock is approved at the evaluation stage. */
export function canRunEvaluateBids(
  t: Pick<TenderActivity, 'bids' | 'gate_states' | 'current_stage'>,
): boolean {
  return evaluationLockApproved(t.gate_states ?? {}) && (t.current_stage ?? 0) >= 5
}

/** Show scored comparison/matrix only after lock approval AND Evaluate Bids has run. */
export function canShowEvaluationResults(
  t: Pick<TenderActivity, 'bids' | 'gate_states' | 'current_stage'>,
): boolean {
  return canRunEvaluateBids(t) && bidsEvaluated(t.bids)
}

// ============================================================================
// The 5 ACTS — a presentation layer over the 10 stages. Each act is a stretch
// of AI work that ends at exactly one human gate (the named step). The backend
// stays 10 stages; this only groups them for the Journey Rail. No new data.
// ============================================================================
export interface TenderAct {
  id: 'define' | 'market' | 'evaluate' | 'award' | 'manage'
  index: number          // 1..5 (human-facing "Act N of 5")
  title: string
  blurb: string          // one-line sub-caption
  stages: number[]       // the 10-stage numbers this act contains
  gate: string           // G1..G5 — the named step that closes the act
  primaryTab: string     // the workbench tab to open when this act is focused
}

export const TENDER_ACTS: TenderAct[] = [
  { id: 'define',   index: 1, title: 'Define',        blurb: 'Business case + route',   stages: [0, 1, 2], gate: 'G1', primaryTab: 'business_case' },
  { id: 'market',   index: 2, title: 'Procure',        blurb: 'Notice + clarifications', stages: [3, 4],    gate: 'G2', primaryTab: 'documents' },
  { id: 'evaluate', index: 3, title: 'Evaluate',      blurb: 'Score · rank · comply',   stages: [5],       gate: 'G3', primaryTab: 'evaluation' },
  { id: 'award',    index: 4, title: 'Award',         blurb: 'Report + contract pack',  stages: [6],       gate: 'G4', primaryTab: 'contract' },
  { id: 'manage',   index: 5, title: 'Manage',        blurb: 'KPIs + performance',      stages: [7, 8, 9], gate: 'G5', primaryTab: 'contract' },
]

/** The act that contains a given stage (defaults to Act 1). */
export function actForStage(stage: number): TenderAct {
  return TENDER_ACTS.find(a => a.stages.includes(stage)) ?? TENDER_ACTS[0]
}

// ============================================================================
// The 6 ACTS of the six-stage lifecycle (plan 02 section 2): Discover, Define,
// Source, Evaluate, Negotiate, Manage. Another presentation layer over the same
// 10 backend stages. TENDER_ACTS above stays as the five-stage layer, used by
// the legacy workbench; the record, the Explorer, the briefing and the hub read
// RECORD_ACTS. `id` is the backend act id (kept for compatibility: Source is
// "market", Negotiate is "award"); `key` is the stage key on screen.
// ============================================================================
/** Which stage vocabulary a classifier uses. Six stages is the default; five is the rollback. */
export type Lifecycle = 'six_stage' | 'five_stage'

export type RecordActKey = 'discover' | 'define' | 'source' | 'evaluate' | 'negotiate' | 'manage'

export interface RecordAct {
  key: RecordActKey
  /** The backend act id. */
  id: 'discover' | 'define' | 'market' | 'evaluate' | 'award' | 'manage'
  index: number
  title: string
  blurb: string
  /** The 10-stage numbers this act contains. */
  stages: number[]
  /** Every gate of the act, in order. */
  gates: string[]
  /** The gate that closes the act. */
  gate: string
  primaryTab: string
}

export const TENDER_ACTS_SIX: RecordAct[] = [
  { key: 'discover',  id: 'discover', index: 1, title: 'Discover',  blurb: 'Contracts + spend baseline',  stages: [0],       gates: ['GD'],       gate: 'GD', primaryTab: 'business_case' },
  { key: 'define',    id: 'define',   index: 2, title: 'Define',    blurb: 'Requirements + standards',    stages: [1],       gates: ['GR'],       gate: 'GR', primaryTab: 'business_case' },
  { key: 'source',    id: 'market',   index: 3, title: 'Source',    blurb: 'Route, pack + clarifications', stages: [2, 3, 4], gates: ['G1', 'G2'], gate: 'G2', primaryTab: 'documents' },
  { key: 'evaluate',  id: 'evaluate', index: 4, title: 'Evaluate',  blurb: 'Compare · comply',            stages: [5],       gates: ['G3'],       gate: 'G3', primaryTab: 'evaluation' },
  { key: 'negotiate', id: 'award',    index: 5, title: 'Negotiate', blurb: 'Brief, positions + award',    stages: [6],       gates: ['G4'],       gate: 'G4', primaryTab: 'contract' },
  { key: 'manage',    id: 'manage',   index: 6, title: 'Manage',    blurb: 'KPIs + performance',          stages: [7, 8, 9], gates: ['G5'],       gate: 'G5', primaryTab: 'contract' },
]

/** The five-stage acts in the RecordAct shape, for the five-stage build. Same data as TENDER_ACTS. */
export const TENDER_ACTS_FIVE_AS_RECORD: RecordAct[] = TENDER_ACTS.map((a) => ({
  key: (a.id === 'market' ? 'source' : a.id === 'award' ? 'negotiate' : a.id) as RecordActKey,
  id: a.id,
  index: a.index,
  title: a.title,
  blurb: a.blurb,
  stages: a.stages,
  gates: [a.gate],
  gate: a.gate,
  primaryTab: a.primaryTab,
}))

/** The acts the record, lists, briefing and hub read. Point this at TENDER_ACTS_FIVE_AS_RECORD to roll back. */
export const RECORD_ACTS: RecordAct[] = TENDER_ACTS_SIX

/** The six gate ids, in the order they are raised (plan 02 section 3). */
export const GATE_SEQUENCE_SIX: readonly string[] = ['GD', 'GR', 'G1', 'G2', 'G3', 'G4', 'G5']

/** The act that contains a backend stage (defaults to the first act). */
export function recordActForStage(stage: number): RecordAct {
  return RECORD_ACTS.find((a) => a.stages.includes(stage)) ?? RECORD_ACTS[0]
}

/** The act a gate belongs to. GD is Discover, GR Define, G1 and G2 Source. Undefined for an unknown gate. */
export function recordActForGate(gateId: string | null | undefined): RecordAct | undefined {
  return gateId ? RECORD_ACTS.find((a) => a.gates.includes(gateId)) : undefined
}

/** The first pending gate in raising order, or null. Reads GD and GR as well as G1 to G5. */
export function pendingGateSix(
  gateStates: Record<string, { status?: string | null } | null | undefined> | null | undefined
): string | null {
  for (const gate of GATE_SEQUENCE_SIX) {
    if (gateStates?.[gate]?.status === 'pending') return gate
  }
  return null
}

// ============================================================================
// Stage colour theming — each workflow stage (act) gets a distinct, strong
// colour, and its underlying sub-tabs inherit the same colour so it is visually
// obvious which tabs belong to which stage. Colours are aligned to the GCF
// Source-to-Contract FRM lifecycle (Plan -> Define -> Procure -> Manage), where
// Evaluate and Award are sub-phases of Procure — so Procure/Evaluate/Award use a
// related hue family (violet/indigo/purple) while Define (blue) and Manage (teal)
// are distinct.
//
// IMPORTANT: every class below is a *literal* string so the Tailwind content
// scanner keeps them — never build these class names via interpolation.
// ============================================================================
export interface StageTheme {
  /** Solid dot / marker background. */
  dot: string
  /** Strong text colour. */
  text: string
  /** Softer text colour. */
  textMuted: string
  /** Border colour for an active/current lane. */
  border: string
  /** Hover border variant (for done lanes). */
  borderHover: string
  /** Light fill for an active/current lane. */
  bg: string
  /** Active tab-button styling. */
  tabActive: string
  /** Inactive tab-button styling. */
  tabInactive: string
}

export const STAGE_THEME: Record<TenderAct['id'], StageTheme> = {
  define: {
    dot: 'bg-blue-500', text: 'text-blue-700', textMuted: 'text-blue-600',
    border: 'border-blue-400', borderHover: 'hover:border-blue-400', bg: 'bg-blue-50',
    tabActive: 'bg-blue-600 text-white shadow-sm', tabInactive: 'text-blue-700 hover:bg-blue-50',
  },
  market: {
    dot: 'bg-violet-500', text: 'text-violet-700', textMuted: 'text-violet-600',
    border: 'border-violet-400', borderHover: 'hover:border-violet-400', bg: 'bg-violet-50',
    tabActive: 'bg-violet-600 text-white shadow-sm', tabInactive: 'text-violet-700 hover:bg-violet-50',
  },
  evaluate: {
    dot: 'bg-indigo-500', text: 'text-indigo-700', textMuted: 'text-indigo-600',
    border: 'border-indigo-400', borderHover: 'hover:border-indigo-400', bg: 'bg-indigo-50',
    tabActive: 'bg-indigo-600 text-white shadow-sm', tabInactive: 'text-indigo-700 hover:bg-indigo-50',
  },
  award: {
    dot: 'bg-purple-500', text: 'text-purple-700', textMuted: 'text-purple-600',
    border: 'border-purple-400', borderHover: 'hover:border-purple-400', bg: 'bg-purple-50',
    tabActive: 'bg-purple-600 text-white shadow-sm', tabInactive: 'text-purple-700 hover:bg-purple-50',
  },
  manage: {
    dot: 'bg-teal-500', text: 'text-teal-700', textMuted: 'text-teal-600',
    border: 'border-teal-400', borderHover: 'hover:border-teal-400', bg: 'bg-teal-50',
    tabActive: 'bg-teal-600 text-white shadow-sm', tabInactive: 'text-teal-700 hover:bg-teal-50',
  },
}

/** Neutral theme for cross-cutting tabs (Overview, Audit Trail). */
export const NEUTRAL_THEME: StageTheme = {
  dot: 'bg-slate-400', text: 'text-brand-navy', textMuted: 'text-brand-muted',
  border: 'border-black/[0.06]', borderHover: 'hover:border-black/[0.12]', bg: 'bg-white',
  tabActive: 'bg-white text-brand-navy shadow-sm', tabInactive: 'text-brand-muted hover:text-brand-navy hover:bg-white/50',
}

/**
 * Maps each workbench sub-tab to its parent workflow stage (act), so tabs can be
 * colour-matched to the stage they belong to. Overview and Audit Trail are
 * cross-cutting and stay neutral.
 */
export const TAB_TO_ACT: Record<string, TenderAct['id'] | null> = {
  overview: null,
  details: 'define',
  business_case: 'define',
  documents: 'market',
  comments: 'market',
  evaluation: 'evaluate',
  compliance: 'evaluate',
  contract: 'award',
  audit: null,
}

/** Theme for a given act id. */
export function stageTheme(actId: TenderAct['id']): StageTheme {
  return STAGE_THEME[actId]
}

/** Theme for a given sub-tab id (neutral when the tab is cross-cutting). */
export function tabTheme(tabId: string): StageTheme {
  const act = TAB_TO_ACT[tabId]
  return act ? STAGE_THEME[act] : NEUTRAL_THEME
}

// ============================================================================
// Stage & tab guidance — short "what is this / expected outcome" copy surfaced
// as pop-ups (stages) and hover boxes (tabs). Modelled on the GCF Source-to-
// Contract FRM stage checklists so non-technical users always know what a stage
// or tab is for and what "done" looks like. Placeholder copy — refine as needed.
// ============================================================================
export interface StageGuidance {
  title: string
  purpose: string
  /** What a successful stage/tab produces. */
  outcome?: string
}

export const STAGE_GUIDANCE: Record<TenderAct['id'], StageGuidance> = {
  define: {
    title: 'Define',
    purpose: 'Establish the business need, map the resources and stakeholders who will support the work, assess the market and set the procurement route and strategy.',
    outcome: 'A fully-defined business case and procurement strategy, with resources and stakeholders mapped.',
  },
  market: {
    title: 'Procure',
    purpose: 'Prepare and publish the tender notice and procurement pack, then manage and respond to bidder clarification questions with equal treatment.',
    outcome: 'A compliant tender in-market with all clarifications logged, answered and issued to bidders.',
  },
  evaluate: {
    title: 'Evaluate',
    purpose: 'Score and rank bids against the published criteria, run compliance and conflict-of-interest checks, and lock the evaluation.',
    outcome: 'A locked, defensible evaluation identifying the most advantageous tender.',
  },
  award: {
    title: 'Award',
    purpose: 'Finalise the award recommendation and contract pack and complete the governance sign-off before contract award.',
    outcome: 'An approved award and a complete contract pack ready for signature.',
  },
  manage: {
    title: 'Manage',
    purpose: 'Onboard the supplier and manage delivery, KPIs, service credits and performance across the contract lifecycle.',
    outcome: 'A mobilised contract with performance actively managed against agreed KPIs.',
  },
}

export const TAB_GUIDANCE: Record<string, StageGuidance> = {
  overview: {
    title: 'Overview',
    purpose: 'At-a-glance status for this tender — total value, authority, current stage and the approval workflow.',
  },
  details: {
    title: 'Tender Details',
    purpose: 'The core tender facts: authority, category, estimated value, key dates and the requirement description.',
  },
  business_case: {
    title: 'Business Case',
    purpose: 'The HMT Five Case business case, the procurement strategy and recommended route, and the resources & stakeholders supporting the procurement.',
    outcome: 'A complete, approvable business case and justified procurement route.',
  },
  documents: {
    title: 'Documents',
    purpose: 'Published notices, the procurement pack and the bidder clarification log — where clarifications are drafted, reviewed and issued.',
    outcome: 'A published procurement pack and clarifications handled fairly.',
  },
  comments: {
    title: 'Comments',
    purpose: 'Team discussion and internal notes on this tender.',
  },
  evaluation: {
    title: 'Evaluation',
    purpose: 'Bid scores, ranking and the most-advantageous-tender selection against the published evaluation model.',
    outcome: 'A scored, ranked and locked evaluation.',
  },
  compliance: {
    title: 'Compliance',
    purpose: 'Policy checks, flags and blocks raised across the tender lifecycle, with their governing rules.',
  },
  contract: {
    title: 'Contract',
    purpose: 'The award recommendation and the editable contract pack (schedules, KPIs and clauses).',
    outcome: 'A finalised contract pack ready for award.',
  },
  audit: {
    title: 'Audit Trail',
    purpose: 'An immutable log of every action and decision taken on this tender.',
  },
}

export type ActState = 'done' | 'current' | 'upcoming'

function actComplete(
  act: TenderAct,
  gateStates: Record<string, GateState>,
  bidsEvaluated: boolean,
): boolean {
  const approved = (g: string) => gateStates?.[g]?.status === 'approved'
  // Evaluate act closes only after Evaluation Lock AND scoring are both done.
  if (act.id === 'evaluate') {
    return approved('G3') && bidsEvaluated
  }
  return approved(act.gate)
}

/**
 * An act is `done` once its gate is approved (and for Evaluate, bids are scored);
 * `current` if it is the earliest not-yet-done act; else `upcoming`.
 */
export function actState(
  act: TenderAct,
  gateStates: Record<string, GateState>,
  bidsEvaluated = false,
): ActState {
  if (actComplete(act, gateStates, bidsEvaluated)) return 'done'
  const earlierAllDone = TENDER_ACTS
    .filter(a => a.index < act.index)
    .every(a => actComplete(a, gateStates, bidsEvaluated))
  return earlierAllDone ? 'current' : 'upcoming'
}

export type GateMark = 'approved' | 'pending' | 'returned' | 'blocked' | 'locked'

/**
 * Stages with an open policy BLOCK that still block the journey rail.
 * BLOCK records on excluded bids remain in the audit trail, but once the
 * gate for that stage is approved they no longer tint the checkpoint red.
 */
export function openBlockedStages(
  t: Pick<TenderActivity, 'current_stage' | 'gate_states' | 'policy_checks'>,
): number[] {
  const gs = t.gate_states || {}
  const stages = new Set<number>()
  for (const c of t.policy_checks ?? []) {
    if (c.result !== 'BLOCK') continue
    const stage = c.stage ?? t.current_stage ?? 0
    const gate = gateForStage(stage)
    if (gate && gs[gate]?.status === 'approved') continue
    stages.add(stage)
  }
  return Array.from(stages)
}

/** The checkpoint state for an act's closing gate (approved clears historical blocks). */
export function gateMarkFor(
  act: TenderAct,
  gateStates: Record<string, GateState>,
  blockedStages: number[] = [],
): GateMark {
  const st = gateStates?.[act.gate]?.status
  if (st === 'approved') return 'approved'
  if (blockedStages.some(s => act.stages.includes(s))) return 'blocked'
  if (st === 'pending') return 'pending'
  if (st === 'returned') return 'returned'
  return 'locked'
}
